import { TableColumnsType } from "antd";
import { SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { Filters } from "src/components/TransactionsGrid/TransactionsGrid";
import { QueryInputRef } from "src/components/QueryInput";
import useTransformDataColumns, {
  DataColumnConfig,
} from "./useTransformDataColumns";
import { constructFilterString } from "src/utility/utility";

interface UseDataTableStateOptions<T extends object> {
  columns: TableColumnsType<T>;
  config: DataColumnConfig<T>;
  itemsPerPage?: number;
  initialFilters?: Filters<T>;
}

export function useDataTableState<T extends object>({
  columns,
  config,
  itemsPerPage = 14,
  initialFilters = {},
}: UseDataTableStateOptions<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters<T>>(initialFilters);
  const [filterQuery, setFilterQuery] = useState<string | null>(null);
  const queryInputRef = useRef<QueryInputRef>(null);

  const filterString = useMemo(() => {
    return filterQuery ?? constructFilterString(filters);
  }, [filterQuery, filters]);

  const handleFilterChange = useCallback(
    (newFilters: SetStateAction<Filters<T>>) => {
      queryInputRef.current?.clear();
      setFilters(newFilters);
      setFilterQuery(null);
      setCurrentPage(1);
    },
    [],
  );

  const handleFilterQueryChange = useCallback((query: string | null) => {
    setFilterQuery(query);
    setFilters({});
    setCurrentPage(1);
  }, []);

  const dataColumns = useTransformDataColumns(
    columns,
    config,
    filters,
    handleFilterChange,
  );

  return {
    currentPage,
    setCurrentPage,
    filters,
    setFilters: handleFilterChange,
    filterQuery,
    setFilterQuery: handleFilterQueryChange,
    queryInputRef,
    dataColumns,
    filterString,
    itemsPerPage,
  };
}
